import { updateModal } from '../../store/app/actions/modalActions'
import { ActionButton } from '../../components/Buttons/ActionButton'
import { updateDepositingAction } from '../../store/deposit/actions'
import { parseUnits } from 'ethers/lib/utils'
import { numberToHex } from '../../utils/number'
import { SUB_ACCOUNT_ID } from '../../config'
import { isUSDStableCoins } from '../../utils/tokens'
import toastify from '../../components/Toastify'
import ModalLink from '../../components/ModalLink'
import { memo } from 'react'
import { useCurrentNetwork, useModal } from '../../store/app/hooks'
import {
  useDepositAmount,
  useDepositing,
  useDepositToken,
} from '../../store/deposit/hook'
import { useWeb3React } from '@web3-react/core'
import { useAppDispatch } from '../../store'
import { styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FlexBetween, FlexCenter, FlexColumn } from '../../styles'
import { useSetAtom } from 'jotai/index'
import { latestDepositExplorerUrlAtom } from '../../components/WaitingForConfirmationModal/atoms'
import { useParams } from 'react-router-dom'
import { useEthereumTokenInfo } from '../../hooks/web3/useEthereumTokenInfo'
import { sendDepositFromEthereum } from '../../hooks/web3/deposit/sendDepositFromEthereum'

const ConfirmOrderWrap = styled('div')`
  padding: 16px;
  color: ${(props) => props.theme.color.text70};
`
const ConfirmOrderTitle = styled(FlexCenter)`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
`
const ConfirmOrderValue = styled(FlexCenter)`
  margin: 16px auto 40px;
  font-weight: 500;
  font-size: 32px;
  line-height: 20px;
  height: 32px;
  span {
    display: flex;
    height: 100%;
    font-size: 14px;
    line-height: 20px;
    align-items: flex-end;
  }
`
const ConfirmOrderList = styled(FlexColumn)`
  padding: 12px;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  margin-bottom: 16px;
  background: ${(props) => props.theme.color.bgLightGray};
`
const ConfirmOrderItem = styled(FlexBetween)`
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  padding: 8px 0;
  width: 100%;
  > div {
    width: 30%;

    &:nth-of-type(2) {
      width: 60%;
      word-wrap: break-word;
      text-align: right;
    }
  }
`
const ConfirmOrderTip = styled('div')`
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 12px;
`
export const DepositConfirmModal = memo(() => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const confirmOrder = useModal('confirmOrder')
  const depositing = useDepositing()
  const { account, provider, chainId } = useWeb3React()
  const currentNetwork = useCurrentNetwork()
  const depositToken = useDepositToken()
  const depositAmount = useDepositAmount()
  const params = useParams<{ address: string }>()

  const { decimals = 0, address: tokenAddress = '' } =
    useEthereumTokenInfo(
      currentNetwork?.layerOneChainId,
      depositToken?.l2CurrencyId!,
    ) ?? {}

  const updateLatestDepositExplorerUrl = useSetAtom(
    latestDepositExplorerUrlAtom,
  )

  return (
    <ModalLink
      width="400px"
      isIn={confirmOrder}
      header={t('deposit-confirm-order')}
      onClose={() => {
        dispatch(
          updateModal({
            modal: 'confirmOrder',
            open: false,
          }),
        )
      }}
    >
      <ConfirmOrderWrap>
        <ConfirmOrderTitle>You will receive</ConfirmOrderTitle>
        <ConfirmOrderValue>
          {depositAmount} <span>{depositToken?.name}</span>
        </ConfirmOrderValue>
        <ConfirmOrderList>
          <ConfirmOrderItem>
            <div>Address</div>
            <div>{params?.address}</div>
          </ConfirmOrderItem>
          <ConfirmOrderItem>
            <div>Network</div>
            <div>{currentNetwork?.name}</div>
          </ConfirmOrderItem>
        </ConfirmOrderList>
        <ConfirmOrderList>
          <ConfirmOrderItem>
            <div>Coin</div>
            <div>{depositToken?.name}</div>
          </ConfirmOrderItem>
          <ConfirmOrderItem>
            <div>Amount</div>
            <div>
              {depositAmount} {depositToken?.name}
            </div>
          </ConfirmOrderItem>
        </ConfirmOrderList>

        <ConfirmOrderTip>
          Ensure that the address is correct and on the same network.
          Transactions cannot be cancelled.
        </ConfirmOrderTip>
        <ActionButton
          loading={depositing}
          variant="contained"
          fullWidth={true}
          onClick={async () => {
            try {
              if (depositing) {
                return
              }
              if (!provider) {
                throw new Error('Invalid provider object')
              }
              if (!currentNetwork?.mainContract) {
                throw new Error('Missing main contract address')
              }
              if (!tokenAddress) {
                throw new Error('Missing token address')
              }
              if (!params?.address) {
                throw new Error('Missing receiver address')
              }
              if (!account) {
                throw new Error('Missing from address')
              }
              if (!chainId) {
                throw new Error(`Unknown chainId ${chainId}`)
              }
              dispatch(updateDepositingAction(true))
              const _amount = parseUnits(depositAmount, decimals)
              const transactionResponse = await sendDepositFromEthereum(
                provider,
                {
                  chainId: numberToHex(chainId),
                  from: account,
                  subAccountId: SUB_ACCOUNT_ID,
                  depositTo: params?.address,
                  token: tokenAddress,
                  amount: _amount,
                  mapping: isUSDStableCoins(depositToken?.id!),
                },
              ).catch((e) => {
                throw new Error(e?.message)
              })
              const { hash } = transactionResponse
              const explorerUrl = `${currentNetwork.explorerUrl}/tx/${hash}`
              if (hash) {
                updateLatestDepositExplorerUrl(explorerUrl)
                toastify.transaction(
                  'Transaction sent successfully',
                  explorerUrl,
                )

                dispatch(
                  updateModal({
                    modal: 'confirmOrder',
                    open: false,
                  }),
                )
              }
            } catch (e) {
              toastify.error(e?.message)
            }
            dispatch(updateDepositingAction(false))
          }}
        >
          {t('cancel-modal-confirm-btn')}
        </ActionButton>
      </ConfirmOrderWrap>
    </ModalLink>
  )
})
